import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { FadeIn } from './ui/FadeIn';
function CompassSVG() {
  return <svg viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" data-id="element-163">
      {/* Outer ring */}
      <circle cx="200" cy="200" r="190" stroke="currentColor" strokeWidth="1.5" opacity="0.3" data-id="element-164" />
      <circle cx="200" cy="200" r="175" stroke="currentColor" strokeWidth="0.5" opacity="0.15" data-id="element-165" />

      {/* Degree tick marks */}
      {Array.from({
      length: 72
    }).map((_, i) => {
      const angle = i * 5 * Math.PI / 180;
      const isMajor = i % 6 === 0;
      const isCardinal = i % 18 === 0;
      const innerR = isCardinal ? 155 : isMajor ? 165 : 172;
      const outerR = 185;
      return <line key={i} x1={200 + innerR * Math.sin(angle)} y1={200 - innerR * Math.cos(angle)} x2={200 + outerR * Math.sin(angle)} y2={200 - outerR * Math.cos(angle)} stroke="currentColor" strokeWidth={isCardinal ? 2 : isMajor ? 1 : 0.5} opacity={isCardinal ? 0.5 : isMajor ? 0.25 : 0.12} data-id="element-166" />;
    })}

      {/* Inner decorative rings */}
      <circle cx="200" cy="200" r="120" stroke="currentColor" strokeWidth="0.5" opacity="0.12" data-id="element-167" />
      <circle cx="200" cy="200" r="80" stroke="currentColor" strokeWidth="0.5" opacity="0.08" data-id="element-168" />

      {/* Compass needle - North (gold/accent) */}
      <polygon points="200,70 212,200 200,188 188,200" fill="#B89B5E" opacity="0.7" data-id="element-169" />
      {/* Compass needle - South (dark) */}
      <polygon points="200,330 212,200 200,212 188,200" fill="currentColor" opacity="0.25" data-id="element-170" />

      {/* East-West crosshair */}
      <polygon points="330,200 200,212 212,200 200,188" fill="currentColor" opacity="0.15" data-id="element-171" />
      <polygon points="70,200 200,212 188,200 200,188" fill="currentColor" opacity="0.15" data-id="element-172" />

      {/* Center circle */}
      <circle cx="200" cy="200" r="14" fill="#B89B5E" opacity="0.5" data-id="element-173" />
      <circle cx="200" cy="200" r="7" fill="#B89B5E" opacity="0.8" data-id="element-174" />
      <circle cx="200" cy="200" r="3" fill="white" opacity="0.9" data-id="element-175" />
    </svg>;
}
export function HeroSection() {
  return <section className="relative min-h-screen w-full flex items-center justify-center bg-ink-900 overflow-hidden" data-id="element-176">
      <div className="absolute inset-0 opacity-20" data-id="element-177">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-radial from-ink-700 to-transparent rounded-full translate-x-1/3 -translate-y-1/3 blur-3xl" data-id="element-178" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-radial from-ink-800 to-transparent rounded-full -translate-x-1/3 translate-y-1/3 blur-3xl" data-id="element-179" />
      </div>

      <div className="absolute inset-0 pointer-events-none" data-id="element-180">
        <div className="absolute left-12 md:left-24 top-0 bottom-0 w-[1px] bg-white/[0.04]" data-id="element-181" />
        <div className="absolute right-12 md:right-24 top-0 bottom-0 w-[1px] bg-white/[0.04]" data-id="element-182" />
      </div>

      {/* Rotating Compass - Right Corner, Big */}
      <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-[15%] pointer-events-none" data-id="element-183">
        <motion.div className="w-[500px] h-[500px] md:w-[700px] md:h-[700px] lg:w-[850px] lg:h-[850px] text-white" initial={{
        opacity: 0,
        scale: 0.7
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 2,
        delay: 0.3,
        ease: 'easeOut'
      }} data-id="element-184">
          <motion.div animate={{
          rotate: 360
        }} transition={{
          repeat: Infinity,
          duration: 60,
          ease: 'linear'
        }} className="w-full h-full" data-id="element-185">
            <CompassSVG data-id="element-186" />
          </motion.div>
        </motion.div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center" data-id="element-187">
        <FadeIn delay={0.2} direction="up" data-id="element-188">
          <span className="inline-block py-1 px-3 border border-accent-400/30 rounded-full text-accent-400 text-xs tracking-[0.2em] uppercase mb-8" data-id="element-189">
            Executive Career Advisory
          </span>
        </FadeIn>

        <FadeIn delay={0.4} direction="up" data-id="element-190">
          <h1 className="md:text-6xl lg:text-7xl font-serif text-sage-50 mb-8 text-[57px]" data-id="element-191">
            Your career is not a transaction. It is a legacy.
          </h1>
        </FadeIn>

        <FadeIn delay={0.6} direction="up" data-id="element-192">
          <p className="text-lg md:text-xl text-sage-300 max-w-2xl mx-auto leading-relaxed mb-12 font-light" data-id="element-193">
            Boardline Executive Advisory partners with senior leaders to
            navigate career transitions with precision, discretion, and
            unmatched expertise. We work until you're placed. That's our
            promise.
          </p>
        </FadeIn>

        <FadeIn delay={0.8} direction="up" data-id="element-194">
          <div className="flex flex-col md:flex-row gap-4 justify-center items-center mb-8" data-id="element-195">
            <Button variant="primary" size="lg" className="min-w-[240px]" data-id="element-196">
              Begin Your Journey
            </Button>
            <Button variant="outline" size="lg" className="min-w-[240px] border-white/20 text-white hover:bg-white/10 hover:text-white hover:border-white" data-id="element-197">
              Learn About Our Services
            </Button>
          </div>

          <p className="text-sm text-sage-300 italic mx-auto max-w-xl leading-relaxed" data-id="element-198">
            "Founded by Simeon Praymond, Executive Placement Specialist — a
            trusted advisor to executives navigating the most pivotal moments of
            their careers."
          </p>

          {/* Credibility Metrics */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12 mt-5 pt-5 border-t border-white/[0.08]" data-id="element-199">
            <div className="flex flex-col items-center text-center" data-id="element-200">
              <span className="text-4xl md:text-5xl font-serif font-bold text-accent-400" data-id="element-201">
                500+
              </span>
              <span className="text-xs uppercase tracking-widest text-sage-400 mt-2" data-id="element-202">
                Executives Placed
              </span>
            </div>

            <div className="hidden md:block w-[1px] h-14 bg-white/[0.1]" data-id="element-203" />
            <div className="md:hidden w-10 h-[1px] bg-white/[0.1]" data-id="element-204" />

            <div className="flex flex-col items-center text-center" data-id="element-205">
              <span className="text-4xl md:text-5xl font-serif font-bold text-accent-400" data-id="element-206">
                20+
              </span>
              <span className="text-xs uppercase tracking-widest text-sage-400 mt-2" data-id="element-207">
                Years Experience
              </span>
            </div>

            <div className="hidden md:block w-[1px] h-14 bg-white/[0.1]" data-id="element-208" />
            <div className="md:hidden w-10 h-[1px] bg-white/[0.1]" data-id="element-209" />

            <div className="flex flex-col items-center text-center" data-id="element-210">
              <span className="text-4xl md:text-5xl font-serif font-bold text-accent-400" data-id="element-211">
                100%
              </span>
              <span className="text-xs uppercase tracking-widest text-sage-400 mt-2" data-id="element-212">
                Placement Commitment
              </span>
            </div>
          </div>
        </FadeIn>
      </div>

      <motion.div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-sage-400" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.5,
      duration: 1
    }} data-id="element-213">
        <span className="text-[10px] uppercase tracking-widest" data-id="element-214">Scroll</span>
        <motion.div animate={{
        y: [0, 10, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut'
      }} data-id="element-215">
          <div className="w-[1px] h-12 bg-gradient-to-b from-accent-400 to-transparent" data-id="element-216" />
        </motion.div>
      </motion.div>
    </section>;
}